/**
 * Transcription progress.
 *
 * The estimate comes from measured throughput on the server, not a timer, so
 * the bar drifts honestly and a stalled job visibly overruns rather than
 * sitting at 99% looking healthy.
 */

export interface Progress {
  status: 'queued' | 'processing' | 'done' | 'failed';
  queuePosition: number;
  elapsedSeconds: number | null;
  estimatedRemainingSeconds: number | null;
  fraction: number | null;
}

function humanise(seconds: number): string {
  if (seconds < 60) return 'under a minute';
  const m = Math.round(seconds / 60);
  if (m < 60) return `about ${m} minute${m === 1 ? '' : 's'}`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `about ${h} hour${h === 1 ? '' : 's'}` : `about ${h}h ${rem}m`;
}

export function TranscriptionProgress({ progress }: { progress: Progress }): JSX.Element | null {
  if (progress.status === 'done') return null;

  if (progress.status === 'failed') {
    return <p className="tx-note">Transcription failed.</p>;
  }

  const label =
    progress.status === 'queued'
      ? progress.queuePosition > 0
        ? `Queued — ${progress.queuePosition} ahead`
        : 'Queued'
      : 'Transcribing';

  const eta =
    progress.estimatedRemainingSeconds !== null
      ? humanise(progress.estimatedRemainingSeconds)
      : null;

  // Queued jobs get an indeterminate bar: the position is known but the share
  // of the wait already served is not, so showing a filled fraction would be
  // inventing a number.
  const indeterminate = progress.status === 'queued' || progress.fraction === null;

  return (
    <div className="tx-progress">
      <div className="tx-progress-head">
        <span>{label}</span>
        {eta && <span className="tx-eta">{eta} left</span>}
      </div>
      <div
        className={indeterminate ? 'tx-bar indeterminate' : 'tx-bar'}
        role="progressbar"
        aria-label="Transcription progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={
          indeterminate ? undefined : Math.round((progress.fraction ?? 0) * 100)
        }
      >
        <span
          className="tx-bar-fill"
          style={indeterminate ? undefined : { width: `${(progress.fraction ?? 0) * 100}%` }}
        />
      </div>
    </div>
  );
}
